/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core'
import { getPasswordStrength, PasswordStrength } from '../../../../utils/password.utils'

@Component({
  selector: 'password-strength-meter',
  templateUrl: './password-strength-meter.component.html',
  host: { display: 'block' },
  styleUrls: ['./password-strength-meter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PasswordStrengthMeterComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef)

  @Output() strengthCriteriaChangedEvent = new EventEmitter()

  // -------------------------------------------------------------------------------------------------------------------
  // @ Getters for password textual strength indicators
  // -------------------------------------------------------------------------------------------------------------------
  get containsLowercaseAndUppercase(): boolean {
    return !!(this.passwordStrength?.contains?.includes('lowercase') && this.passwordStrength?.contains?.includes('uppercase'))
  }

  get containsSymbols(): boolean {
    return !!this.passwordStrength?.contains?.includes('symbol')
  }

  get containsNumbers(): boolean {
    return !!this.passwordStrength?.contains?.includes('number')
  }

  get hasMinLength(): boolean {
    return (this.passwordStrength?.length ?? 0) >= 8
  }

  private readonly _numberOfStrengthMeterItems = 6
  private readonly _strengthClasses = ['too-weak', 'weak', 'medium', 'strong']

  private _password!: string
  @Input() set password(newPassword: string) {
    this._password = newPassword
    this._updateStrengthMeter(this._password)
  }

  get password(): string {
    return this._password
  }

  @Input() passwordCriteriaVisible = false
  public passwordStrength!: PasswordStrength
  public strengthItems = Array(this._numberOfStrengthMeterItems).fill(this._strengthClasses[0])

  // Translation key for the currently announced strength bucket. Updated only when the bucket
  // changes — gives screen-reader users a per-keystroke-quiet, per-bucket-loud announcement.
  // Stays null until the user types something so we don't announce "weak" on initial render.
  public strengthAnnouncementLabelKey: string | null = null
  private _lastAnnouncedStrengthId: number | undefined

  // -------------------------------------------------------------------------------------------------------------------
  // @ LIFECYCLE-METHODS
  // -------------------------------------------------------------------------------------------------------------------
  async ngOnInit(): Promise<void> {
    await this._updateStrengthMeter(this.password)
  }

  // -------------------------------------------------------------------------------------------------------------------
  // @ PRIVATE METHODS
  // -------------------------------------------------------------------------------------------------------------------
  private async _updateStrengthMeter(password: string): Promise<void> {
    this.passwordStrength = getPasswordStrength(password)

    // id is a number and corresponds to 0: too weak, 1: weak, 2: medium and 3: strong
    if (this.passwordStrength.id === 0) {
      // In case the password is too weak/not filled we need x grey strength indicators
      this.strengthItems = Array(this._numberOfStrengthMeterItems).fill(this._strengthClasses[0])
    } else {
      // In case the password is > too weak we need the indicators based on the strength and fill the rest up with grey ones
      // As we've currently got 6 strength indicators and the scale of the pw strength is set to 0-3 we multiply by 2
      this.strengthItems = Array(this.passwordStrength.id! * 2).fill(this._strengthClasses[this.passwordStrength.id!])

      if (this.strengthItems.length < this._numberOfStrengthMeterItems) {
        const fillUp = this._numberOfStrengthMeterItems - this.strengthItems.length
        for (let i = 0; i < fillUp; i++) {
          this.strengthItems.push(this._strengthClasses[0])
        }
      }
    }

    this.strengthCriteriaChangedEvent.emit({
      containsLowercaseAndUppercase: this.containsLowercaseAndUppercase,
      containsSymbols: this.containsSymbols,
      containsNumbers: this.containsNumbers,
      hasMinLength: this.hasMinLength,
    })

    this._maybeAnnounceStrength()

    this._cdr.markForCheck()
  }

  private _maybeAnnounceStrength(): void {
    const id = this.passwordStrength?.id
    if (id === undefined) {
      return
    }
    if (!this._password) {
      this._lastAnnouncedStrengthId = id
      this.strengthAnnouncementLabelKey = null
      return
    }
    if (id === this._lastAnnouncedStrengthId) {
      return
    }
    this._lastAnnouncedStrengthId = id
    this.strengthAnnouncementLabelKey = this._strengthLabelKeyForId(id)
  }

  private _strengthLabelKeyForId(id: number): string {
    switch (id) {
      case 2:
        return 'signup.password.strength.indicator.mediumLabel'
      case 3:
        return 'signup.password.strength.indicator.strongLabel'
      default:
        return 'signup.password.strength.indicator.weakLabel'
    }
  }

  // -------------------------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -------------------------------------------------------------------------------------------------------------------
  public displayPasswordCriteria() {
    this.passwordCriteriaVisible = true
    this._cdr.markForCheck()
  }
}
